import type { ProductTaxonomyRenameDefinition } from '../types';

export const profilesRename = {
  terms: {
    people: {
      current: {
        singular: 'person',
        plural: 'people',
        titleSingular: 'Person',
        titlePlural: 'People',
      },
      future: {
        singular: 'profile',
        plural: 'profiles',
        titleSingular: 'Profile',
        titlePlural: 'Profiles',
      },
      // FPS-3782: the People rename was withdrawn for the end state, so `final` keeps the current
      // forms. The transitional stage still reads Profiles, which is what the rename tooltips and
      // help docs describe.
      final: {
        singular: 'person',
        plural: 'people',
        titleSingular: 'Person',
        titlePlural: 'People',
      },
    },
    // "Customer(s)" is used interchangeably with "People" for the audience noun today; the rename
    // standardizes both on "Profile(s)". Same future forms as `people`, distinct current forms so
    // flag-off output stays byte-identical.
    customers: {
      current: {
        singular: 'customer',
        plural: 'customers',
        titleSingular: 'Customer',
        titlePlural: 'Customers',
      },
      future: {
        singular: 'profile',
        plural: 'profiles',
        titleSingular: 'Profile',
        titlePlural: 'Profiles',
      },
    },
  },
  copy: {
    'nav.manageAudience.people': {
      term: 'people.titlePlural',
    },
    // Relative pronoun for the people noun: "people who match" reads naturally, but once renamed a
    // profile is a thing, not a person, so it becomes "profiles that match". Compose this after the
    // people term in copy like `{people.plural} {people.relativePronoun} match`.
    'people.relativePronoun': {
      current: 'who',
      future: 'that',
      final: 'who',
    },
    // Doubled-noun guard: "customer profiles" must NOT become "profile profiles".
    // Flag-off: "Enrich your customer profiles" → flag-on: "Enrich your profiles"
    'home.enrichProfilesCardTitle': {
      current: 'Enrich your customer profiles',
      future: 'Enrich your profiles',
    },
    // The People list page's H1: during the transitional rename stage it appends the former name
    // to the new one (mirrors Ember's `profiles.pageTitleWithFormerName`). Used only on the main
    // page header title — other surfaces keep the plain `people` term. The `final` stage drops the
    // rename altogether and shows the plain current name.
    'profiles.pageTitleWithFormerName': {
      current: 'People',
      future: 'Profiles (People)',
      final: 'People',
    },
    // Display value for the "Use Customer Zone" boolean field in audit logs
    'auditLogs.useCustomerTimezoneTitle': {
      current: 'Use Customer Timezone',
      future: 'Use Profile Timezone',
    },
    // Billing uses the compound "people profiles" / "People Profiles" today; under the rename a
    // profile already implies a person, so it collapses to just "profiles" / "Profiles" rather than
    // the doubled "profiles profiles". Used by the Your Plan usage/messaging displays.
    'billing.profilesUnit': {
      current: 'people profiles',
      future: 'profiles',
    },
    'billing.profilesUnitTitle': {
      current: 'People Profiles',
      future: 'Profiles',
    },
    // "Billable People Count" / "Billable Profile Count" in the billing-help sidebar. Singular, because
    // it qualifies "Count" rather than standing alone. Mirrors Ember's key of the same name.
    'billing.billableCountNoun': {
      current: 'People',
      future: 'Profile',
    },
  },
  routes: {
    // The Journeys (Ember) list URL, and the migrated Hydra list, add-people catalog, add-a-person
    // form and merge wizard URLs. All of them rename in the transitional stage and go back to
    // `people` in the end state (FPS-3782). The migrated `welcome` URL has no entry: it only ever
    // redirects, to `people.list` or to its Ember twin, so nothing ever builds a link to it.
    'people.index': {
      current: '/workspaces/:workspaceId/journeys/people',
      future: '/workspaces/:workspaceId/journeys/profiles',
      final: '/workspaces/:workspaceId/journeys/people',
    },
    'people.list': {
      current: '/workspaces/:workspaceId/people',
      future: '/workspaces/:workspaceId/profiles',
      final: '/workspaces/:workspaceId/people',
    },
    'people.create': {
      current: '/workspaces/:workspaceId/people/create',
      future: '/workspaces/:workspaceId/profiles/create',
      final: '/workspaces/:workspaceId/people/create',
    },
    'people.new': {
      current: '/workspaces/:workspaceId/people/new',
      future: '/workspaces/:workspaceId/profiles/new',
      final: '/workspaces/:workspaceId/people/new',
    },
    'people.merge': {
      current: '/workspaces/:workspaceId/people/merge',
      future: '/workspaces/:workspaceId/profiles/merge',
      final: '/workspaces/:workspaceId/people/merge',
    },
  },
  sideNavItemIds: {
    people: 'profiles',
  },
} as const satisfies ProductTaxonomyRenameDefinition;
